import {useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {getRememberPreference, login, setRememberPreference} from './api';
import LanguageSwitcher from './LanguageSwitcher';
import './Login.css';

function EyeIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
        </svg>
    );
}

function EyeOffIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
            <line x1="1" y1="1" x2="23" y2="23"/>
        </svg>
    );
}

// Authenticates against POST /api/v0/auth/login; on success the JWT access and refresh
// tokens are stored by `login` and onLogin is invoked. "Remember me" decides whether the
// tokens persist across browser restarts (localStorage) or are dropped when the browser
// closes (sessionStorage); it defaults to off so credentials are not left on a shared
// computer by accident.
function Login({onLogin}) {
    const {t} = useTranslation();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(getRememberPreference);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            await login(username, password, rememberMe);
            onLogin();
        } catch (err) {
            setError(err.message || t('auth.login.failed'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-box">
                <div className="auth-language-switcher">
                    <LanguageSwitcher disabled={isLoading} />
                </div>
                <img
                    src={`${process.env.PUBLIC_URL}/logo512.png`}
                    alt="AutoLang logo"
                    className="auth-logo"
                />
                <h1>{t('common.appName')}</h1>
                <h2>{t('auth.login.title')}</h2>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="username">{t('auth.login.username')}</label>
                        <input
                            type="text"
                            id="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            disabled={isLoading}
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="password">{t('auth.login.password')}</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={isLoading}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword((shown) => !shown)}
                                disabled={isLoading}
                                aria-label={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
                                aria-pressed={showPassword}
                                title={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
                            >
                                {showPassword ? <EyeOffIcon/> : <EyeIcon/>}
                            </button>
                        </div>
                    </div>
                    <label className="remember-me">
                        <input
                            type="checkbox"
                            checked={rememberMe}
                            onChange={(e) => {
                                setRememberMe(e.target.checked);
                                setRememberPreference(e.target.checked);
                            }}
                            disabled={isLoading}
                        />
                        {t('auth.login.rememberMe')}
                    </label>
                    {error && <div className="error-message">{error}</div>}
                    <button type="submit" disabled={isLoading}>
                        {isLoading ? t('auth.login.submitting') : t('auth.login.submit')}
                    </button>
                </form>
                <div className="switch-auth">
                    {t('auth.login.noAccount')}{' '}
                    <Link to="/register" className="link-button">
                        {t('auth.login.registerLink')}
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default Login;
